// Connexion et déconnexion web (ACC-01, ACC-02, ADR 0023) : cookie CSRF puis session Sanctum. La redirection après
// connexion n'accepte que des chemins internes (pas de redirection ouverte).
import type { QueryClient } from '@tanstack/react-query';
import { v1AuthLogin, v1AuthLogout, v1AuthTwoFactorChallenge, v1AuthTwoFactorResend } from '@/api/generated/endpoints/auth/auth';
import { getV1MeShowQueryKey } from '@/api/generated/endpoints/me/me';
import type { MeResource, TwoFactorChallengeResource } from '@/api/generated/model';
import { ensureCsrfCookie } from '@/api/http';
import { session } from './session';

export const LOGIN_PATH = '/connexion';
export const TWO_FACTOR_PATH = '/connexion/verification';
export const SECURITY_PATH = '/moi/securite';

/**
 * Résultat de la connexion (ACC-14, lot F13e) : connecté (avec, pour un profil sensible sans 2FA, l'obligation de la
 * configurer), ou second facteur attendu (défi de ACC-P02, aucune session encore ouverte).
 */
export type SignInResult = { kind: 'signed_in'; me: MeResource; setupRequired: boolean } | { kind: 'two_factor'; challenge: TwoFactorChallengeResource };

export async function signIn(identifier: string, password: string): Promise<SignInResult> {
  await ensureCsrfCookie();
  const response = await v1AuthLogin({ identifier: identifier.trim(), password });
  session.reset();
  const { user, two_factor: challenge, two_factor_setup_required: setupRequired } = response.data;
  if (challenge) {
    saveChallenge(challenge);
    return { kind: 'two_factor', challenge };
  }
  if (!user) throw new Error('Réponse de connexion incomplète.');
  return { kind: 'signed_in', me: user, setupRequired };
}

/** ACC-P02 — valider le second facteur (code ou code de secours) ; la session est ouverte. */
export async function completeTwoFactor(challengeToken: string, input: { code?: string; recovery_code?: string }): Promise<MeResource> {
  await ensureCsrfCookie();
  const response = await v1AuthTwoFactorChallenge({ challenge_token: challengeToken, ...input });
  clearChallenge();
  session.reset();
  if (!response.data.user) throw new Error('Réponse de connexion incomplète.');
  return response.data.user;
}

export async function resendTwoFactor(challengeToken: string): Promise<{ expires_at: string; resend_available_at: string | null }> {
  await ensureCsrfCookie();
  const response = await v1AuthTwoFactorResend({ challenge_token: challengeToken });
  return response.data;
}

// Défi en cours (15 min) : conservé pour l'onglet, le temps de saisir le code (jamais le code lui-même).
const CHALLENGE_KEY = 'wangre.two-factor';

export function saveChallenge(challenge: TwoFactorChallengeResource): void {
  try {
    sessionStorage.setItem(CHALLENGE_KEY, JSON.stringify(challenge));
  } catch {
    // stockage indisponible : la page de vérification redemandera la connexion
  }
}

export function readChallenge(): TwoFactorChallengeResource | null {
  try {
    const raw = sessionStorage.getItem(CHALLENGE_KEY);
    const value = raw ? (JSON.parse(raw) as TwoFactorChallengeResource) : null;
    return value && Date.parse(value.expires_at) > Date.now() ? value : null;
  } catch {
    return null;
  }
}

export function clearChallenge(): void {
  try {
    sessionStorage.removeItem(CHALLENGE_KEY);
  } catch {
    // rien à effacer
  }
}

/** Met en cache l'utilisateur renvoyé par la connexion (évite un aller-retour GET /me). */
export function primeMe(queryClient: QueryClient, me: MeResource): void {
  queryClient.setQueryData(getV1MeShowQueryKey(), { data: me });
}

export async function signOut(queryClient: QueryClient): Promise<void> {
  try {
    await ensureCsrfCookie();
    await v1AuthLogout();
  } finally {
    queryClient.clear();
    session.reset();
    // Messagerie hors ligne (COM-09) : rien ne reste sur le poste après la déconnexion.
    void import('@/modules/comms/offline').then(({ clearOfflineMessaging }) => clearOfflineMessaging()).catch(() => undefined);
  }
}

/** Chemin de retour après connexion : interne à l'application, jamais une page d'accès. */
export function safeRedirect(target: unknown): string {
  if (typeof target !== 'string' || !target.startsWith('/') || target.startsWith('//')) return '/';
  if (PUBLIC_PATHS.some((path) => target.startsWith(path))) return '/';
  return target;
}

/** Chemins publics (A14) : jamais protégés par la garde, jamais cibles de retour. */
export const PUBLIC_PATHS = ['/connexion', '/mot-de-passe', '/activation', '/compte', '/deconnexion', '/inscription'];

export function loginUrl(currentPath: string): string {
  const redirect = safeRedirect(currentPath);
  return redirect === '/' ? LOGIN_PATH : `${LOGIN_PATH}?redirect=${encodeURIComponent(redirect)}`;
}
